"use client";

import type {
  ImageContent,
  PlaceholderValues,
  Rating,
} from "@homeinstead/schemas";
import type { CSSProperties } from "react";
import type { FormRegularStep } from "../form-types";
import type { HeroVariantStyles } from "../hero-variant-styles";

import { isAccentTextFillAnimation } from "@homeinstead/schemas";
import { cn } from "@homeinstead/ui";
import { getHeroVariantStyles } from "../../CareSeeker/shared/hero-variants";
import { ContinueButton } from "../ContinueButton";
import { LeadFormRichTextBlockFormItem } from "../LeadFormRichTextBlockFormItem";
import { defaultLeadFormStyleClasses } from "../lead-form-styles";
import { StepInputRenderer } from "../StepInputRenderer";
import { useLeadFormStepKeyboardAdvance } from "../use-lead-form-step-keyboard-advance";
import { ButtonStackHeroImage } from "./ButtonStackHeroImage";
import { ButtonStackRadioPills } from "./ButtonStackRadioPills";
import { ButtonStackRatingStrip } from "./ButtonStackRatingStrip";
import { mapButtonStackStep } from "./map-button-stack-step";

type RichTextSpan = {
  _type: string;
  marks?: string[];
  text?: string;
};

type RichTextBlock = {
  _type: string;
  children?: RichTextSpan[];
  markDefs?: { _key: string; _type: string; animation?: string }[];
  style?: string;
};

const HEADING_STYLES = new Set(["h1", "h2", "h3", "h4", "h5", "h6"]);

/**
 * Same clock as AccentText accent-fill: 500ms before the first letter,
 * 32ms between letters, then the 300ms color fill (heading color → accent).
 * Each span is its own AccentText, so letter indexes restart and phrases in
 * one heading run together. The rating waits for the longest heading span.
 * Marks other than that accent nest another element, and AccentText leaves
 * those solid. Accent text outside h1–h6 does not animate. The stylesheet
 * applies this delay from the sm breakpoint up, where the letter animation
 * runs.
 */
const ACCENT_TEXT_ANIMATION_START_MS = 500;
const ACCENT_TEXT_ANIMATION_STAGGER_MS = 32;
const ACCENT_TEXT_ANIMATION_DURATION_MS = 300;

function getRatingDelayAfterAccent(headlineText: unknown) {
  const characters = getLongestAccentTextAnimationSpan(headlineText);
  if (characters === 0) {
    return "0ms";
  }

  return `${ACCENT_TEXT_ANIMATION_START_MS + (characters - 1) * ACCENT_TEXT_ANIMATION_STAGGER_MS + ACCENT_TEXT_ANIMATION_DURATION_MS}ms`;
}

function getLongestAccentTextAnimationSpan(headlineText: unknown) {
  if (!Array.isArray(headlineText)) {
    return 0;
  }

  let longest = 0;

  for (const block of headlineText) {
    if (!isHeadingBlock(block)) {
      continue;
    }

    const accentKeys = new Set(
      (block.markDefs ?? [])
        .filter(
          (mark) =>
            mark._type === "accentText" &&
            isAccentTextFillAnimation(mark.animation),
        )
        .map((mark) => mark._key),
    );

    if (accentKeys.size === 0) {
      continue;
    }

    for (const child of block.children ?? []) {
      longest = Math.max(
        longest,
        getAccentTextAnimationSpanLength(child, accentKeys),
      );
    }
  }

  return longest;
}

function getAccentTextAnimationSpanLength(
  child: RichTextSpan,
  accentKeys: Set<string>,
) {
  if (child._type !== "span" || typeof child.text !== "string") {
    return 0;
  }

  const marks = child.marks ?? [];
  const [onlyMark] = marks;
  if (marks.length !== 1 || !onlyMark || !accentKeys.has(onlyMark)) {
    return 0;
  }

  return Array.from(child.text).length;
}

function isHeadingBlock(value: unknown): value is RichTextBlock {
  return (
    typeof value === "object" &&
    value !== null &&
    "_type" in value &&
    value._type === "block" &&
    "style" in value &&
    typeof value.style === "string" &&
    HEADING_STYLES.has(value.style)
  );
}

type ButtonStackHeroStepProps = {
  errors: Record<string, string[]>;
  heroImageLarge?: ImageContent | null;
  heroImageSmall?: ImageContent | null;
  isActive: boolean;
  /**
   * True when LeadFormStepWrapper has bled past the section padding. A
   * full-width wrapper cancels that bleed. The copy column keeps its own
   * inset, because that hero padding would swallow the column.
   */
  isStepWrapperHeroMode?: boolean;
  isLastStep: boolean;
  isPending: boolean;
  officeRating?: Omit<Rating, "_type">;
  onContinue: () => void;
  onFieldChange: (name: string, value: string) => void;
  onPostalLocationChange: (values: {
    postalCode: string;
    location: string;
  }) => void;
  onValidationError: (errors: Record<string, string[]>) => void;
  phoneNumber?: string;
  placeholderValues?: PlaceholderValues;
  postalLocationValues: { postalCode: string; location: string };
  step: FormRegularStep;
  stepUserSelection?: Record<string, string | string[]>;
};

const lightHeroStyles: HeroVariantStyles = getHeroVariantStyles("light");

function ButtonStackHeroStep({
  errors,
  heroImageLarge,
  heroImageSmall,
  isActive,
  isStepWrapperHeroMode = false,
  isLastStep,
  isPending,
  officeRating,
  onContinue,
  onFieldChange,
  onPostalLocationChange,
  onValidationError,
  phoneNumber,
  placeholderValues,
  postalLocationValues,
  step,
  stepUserSelection,
}: ButtonStackHeroStepProps) {
  const stepAnalyticsName = step.metricsLabel ?? step.id;
  const stepHasExplicitButton =
    step.inputs?.some(
      (input) =>
        input._type === "formButton" || input._type === "multiSelectInput",
    ) ?? false;
  const { stepHandleContinue, stepHasAutoAdvance } =
    useLeadFormStepKeyboardAdvance({
      isActive,
      isPending,
      onContinue,
      onValidationError,
      postalLocationValues,
      step,
      stepAnalyticsName,
      stepUserSelection,
    });
  const mapping = mapButtonStackStep(step);

  // The image and gradient exist only while step 1 is showing. Later steps
  // render through LeadFormStepItem, so leaving this step unmounts the shell.
  if (!isActive || mapping.kind === "fallback") {
    return null;
  }

  const flowProps = {
    errors: isActive ? errors : undefined,
    heroVariantStyles: lightHeroStyles,
    isSubmitting: isPending && isLastStep,
    officeRating,
    onContinue: stepHandleContinue,
    onFieldChange,
    onPostalLocationChange,
    phoneNumber,
    placeholderValues,
    stepId: step.id,
    stepName: stepAnalyticsName,
    stepUserSelection,
  };

  return (
    // Small viewports stack copy above the photo (Figma). From md the photo is the
    // full-bleed backdrop; flex-col-reverse is dropped so absolute positioning
    // can pin the image behind the copy column. This is earlier than the
    // lead form's xl hero, because the photo does not need that two-column grid.
    <section
      className="relative flex flex-col-reverse overflow-hidden bg-new-beginnings-base text-white md:block md:min-h-[48.5rem]"
      data-button-stack-hero=""
      style={
        {
          "--button-stack-rating-delay": getRatingDelayAfterAccent(
            mapping.headline?.text,
          ),
        } as CSSProperties
      }
    >
      <div className="relative h-[22rem] md:absolute md:inset-0 md:h-auto">
        <ButtonStackHeroImage
          heroImageLarge={
            heroImageLarge ??
            (step.infoPanel?.mode === "hero"
              ? step.infoPanel.heroImageLarge
              : undefined)
          }
          heroImageSmall={
            heroImageSmall ??
            (step.infoPanel?.mode === "hero"
              ? step.infoPanel.heroImageSmall
              : undefined)
          }
        />
        {/* Small viewports keep a solid green copy band; the left-fade gradient is desktop-only. */}
        <div className="pointer-events-none absolute inset-0 hidden bg-gradient-to-r from-[20%] from-new-beginnings-base via-[45%] via-new-beginnings-base/80 to-transparent md:block" />
        {mapping.reviewStars ? (
          <ButtonStackRatingStrip
            input={mapping.reviewStars}
            officeRating={officeRating}
          />
        ) : null}
      </div>

      <div
        className={cn(
          "relative z-10 w-full min-w-0",
          isStepWrapperHeroMode &&
            defaultLeadFormStyleClasses.stepContentHeroHorizontalPadding,
        )}
      >
        {/* Headline is 437px. The line under it is 401px. Pills stay 368px.
            Below md that column is narrower than the band, so it centers.
            The backdrop layout pins the same column to the start edge. */}
        <div className="mx-auto flex w-full min-w-0 max-w-[27.3125rem] flex-col gap-5 px-6 py-8 md:mx-0 md:px-0 md:pt-[3.75rem] md:pb-16">
          <div className="[&_br]:hidden [&_h1]:text-xl md:[&_h1]:text-1xl [&_h4]:mt-2 [&_h4]:max-w-[25.0625rem] [&_p]:mt-2 [&_p]:max-w-[25.0625rem] [&_p]:font-bold [&_p]:font-jakarta [&_p]:text-md">
            {mapping.headline ? (
              <LeadFormRichTextBlockFormItem
                {...flowProps}
                input={mapping.headline}
              />
            ) : null}

            {mapping.subhead ? (
              <LeadFormRichTextBlockFormItem
                {...flowProps}
                input={mapping.subhead}
              />
            ) : null}

            {mapping.flowBefore.map((input) => (
              <StepInputRenderer
                input={input}
                key={input._key}
                {...flowProps}
              />
            ))}
          </div>

          <div className="flex w-full min-w-0 max-w-[23rem] flex-col gap-5 pt-6 [&_a]:h-14 [&_a]:rounded-full [&_button]:h-14 [&_button]:rounded-full">
            <ButtonStackRadioPills
              heroVariantStyles={lightHeroStyles}
              onContinue={stepHandleContinue}
              onFieldChange={onFieldChange}
              radioInput={mapping.radioInput}
              stepName={stepAnalyticsName}
            />

            {mapping.trailingItems.map((input) => (
              <StepInputRenderer
                input={input}
                key={input._key}
                {...flowProps}
              />
            ))}

            {/* The radio advances on its own only when autoAdvance is set. Otherwise
              this step needs the same fallback Continue button as LeadFormStepItem,
              unless a form button or multi-select already owns that action. */}
            {!stepHasExplicitButton && !stepHasAutoAdvance ? (
              <ContinueButton
                analyticsStepName={stepAnalyticsName}
                analyticsUserSelection={stepUserSelection}
                disabled={isPending}
                onContinue={stepHandleContinue}
                variant={lightHeroStyles.continueButtonVariant}
              />
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}

export { ButtonStackHeroStep };
